import { createFileRoute, useNavigate } from "@tanstack/react-router";
import { useState, useCallback, useEffect } from "react";
import { motion, AnimatePresence } from "framer-motion";
import { ArrowLeft, ArrowRight, Sparkles, CalendarIcon, Rocket, CloudRain, Droplets, Thermometer } from "lucide-react";
import { format } from "@/lib/date-format";
import { type WizardAnswers, type DurationPreference, type WeatherExposure, DEFAULT_WIZARD_ANSWERS } from "@/lib/wizard-types";
import { geocodeZip } from "@/lib/geocode.functions";
import { logWizardResponse } from "@/lib/wizard-log.functions";
import { useServerFn } from "@tanstack/react-start";
import { Textarea } from "@/components/ui/textarea";
import { Calendar } from "@/components/ui/calendar";
import { Popover, PopoverContent, PopoverTrigger } from "@/components/ui/popover";
import { cn } from "@/lib/utils";
import { getWeatherForecast, type WeatherForecast } from "@/lib/weather.functions";
import { buildSeoMeta } from "@/lib/seo";

export const Route = createFileRoute("/discover")({
  head: () => buildSeoMeta({
    title: "Find Things To Do In Lakewood Ranch — RanchPlanner",
    description:
      "Tell us what you're in the mood for and we'll find the perfect things to do in Lakewood Ranch, FL — events, parks, family fun, and more.",
    path: "/discover",
  }),
  component: DiscoverPage,
});

const STEPS = [
  { key: "timeframe", title: "When are you heading out?", subtitle: "Tap one to continue" },
  { key: "duration", title: "How much time do you have?", subtitle: "Tap one to continue" },
  { key: "budget", title: "Per-person budget?", subtitle: "Tap one to continue" },
  { key: "weather", title: "Indoors or outdoors?", subtitle: "Pick your vibe — here's the forecast for your dates" },
  { key: "origin", title: "Where are you starting from?", subtitle: "Defaults to 34202 — type your ZIP to override" },
  { key: "extras", title: "Anything else we should know?", subtitle: "Passes, memberships, recent outings, likes & dislikes — and if you want us to prioritize kid-friendly picks (or skip them), just say so. Leave blank to skip." },
];

interface QuickOption {
  emoji: string;
  label: string;
  sublabel?: string;
}

function getWeekendDates() {
  const now = new Date();
  const day = now.getDay();
  if (day === 0) return { start: new Date(now), end: new Date(now) };
  if (day === 6) {
    const sun = new Date(now);
    sun.setDate(now.getDate() + 1);
    return { start: new Date(now), end: sun };
  }
  const sat = new Date(now);
  sat.setDate(now.getDate() + (6 - day));
  const sun = new Date(sat);
  sun.setDate(sat.getDate() + 1);
  return { start: sat, end: sun };
}

const TIMEFRAME_OPTIONS: (QuickOption & { getRange: () => { start: string; end: string } })[] = [
  { emoji: "📍", label: "Today", getRange: () => { const d = new Date().toISOString().split("T")[0]; return { start: d, end: d }; } },
  { emoji: "🌅", label: "Tomorrow", getRange: () => { const d = new Date(Date.now() + 86400000).toISOString().split("T")[0]; return { start: d, end: d }; } },
  {
    emoji: "🗓️", label: "This Weekend",
    sublabel: (() => {
      const { start, end } = getWeekendDates();
      if (start.toDateString() === end.toDateString()) return start.toLocaleDateString("en-US", { weekday: "long", month: "short", day: "numeric" });
      return `${start.toLocaleDateString("en-US", { month: "short", day: "numeric" })}–${end.toLocaleDateString("en-US", { month: "short", day: "numeric" })}`;
    })(),
    getRange: () => {
      const { start, end } = getWeekendDates();
      return { start: start.toISOString().split("T")[0], end: end.toISOString().split("T")[0] };
    },
  },
];

const DURATION_OPTIONS: (QuickOption & { value: DurationPreference })[] = [
  { emoji: "⚡", label: "Quick stop", sublabel: "Under 2 hours", value: "short" },
  { emoji: "🕐", label: "Half day", sublabel: "2–5 hours", value: "half" },
  { emoji: "🌞", label: "Whole day", sublabel: "5+ hours", value: "full" },
  { emoji: "♾️", label: "Show all", sublabel: "No time limit", value: "any" },
];

const BUDGET_VIBE_OPTIONS: (QuickOption & { budgetExclude: string[] })[] = [
  { emoji: "🆓", label: "Free", budgetExclude: ["low", "medium", "high"] },
  { emoji: "💵", label: "Under $25", sublabel: "Per person", budgetExclude: ["medium", "high"] },
  { emoji: "💸", label: "Under $75", sublabel: "Per person", budgetExclude: ["high"] },
  { emoji: "♾️", label: "No Limit", budgetExclude: [] },
];

function toLocalDate(date: Date) {
  return new Date(date.getFullYear(), date.getMonth(), date.getDate());
}

function toDateString(date: Date) {
  return toLocalDate(date).toISOString().split("T")[0];
}

function OptionButton({ emoji, label, sublabel, selected, onClick }: QuickOption & { selected: boolean; onClick: () => void }) {
  return (
    <button
      onClick={onClick}
      className={`flex items-center gap-3 w-full px-4 py-3 rounded-xl border border-accent/20 text-left transition-all text-foreground ${
        selected
          ? "bg-accent/25 font-bold shadow-sm"
          : "bg-accent/10 hover:bg-accent/25"
      }`}
    >
      <span className="text-2xl">{emoji}</span>
      <div>
        <span className="text-sm font-semibold block leading-tight">{label}</span>
        {sublabel && <span className="text-xs opacity-70">{sublabel}</span>}
      </div>
    </button>
  );
}

function DiscoverPage() {
  const [step, setStep] = useState(0);
  const [answers, setAnswers] = useState<WizardAnswers>(DEFAULT_WIZARD_ANSWERS);
  const [showDatePicker, setShowDatePicker] = useState(false);
  const [dateRange, setDateRange] = useState<{ from?: Date; to?: Date }>({});
  const [budgetChosen, setBudgetChosen] = useState(false);
  const [weather, setWeather] = useState<WeatherForecast | null>(null);
  const [weatherLoading, setWeatherLoading] = useState(false);
  const [exposureTouched, setExposureTouched] = useState(false);
  // --- Origin step (ZIP + max drive) ---
  const [zipInput, setZipInput] = useState("");
  const DEFAULT_ZIP = "34202";
  const [zipLoading, setZipLoading] = useState(false);
  const [zipError, setZipError] = useState<string | null>(null);
  const [maxDriveMinutes, setMaxDriveMinutes] = useState<number>(60);
  const geocodeZipFn = useServerFn(geocodeZip);
  const logWizardFn = useServerFn(logWizardResponse);
  const navigate = useNavigate();
  const totalSteps = STEPS.length;

  const update = useCallback(
    (patch: Partial<WizardAnswers>) => setAnswers((prev) => ({ ...prev, ...patch })),
    [],
  );

  const advance = useCallback(() => {
    setStep((s) => Math.min(s + 1, totalSteps - 1));
  }, [totalSteps]);

  const handleFinish = () => {
    if (typeof window !== "undefined") {
      sessionStorage.setItem("rp-wizard", JSON.stringify(answers));

      // Derive a semantic timeframe label for back-compat (today/tomorrow/weekend/custom).
      // Stats now use the actual start/end dates, but the label remains useful at-a-glance.
      const todayISO = new Date().toISOString().split("T")[0];
      const tomorrowISO = new Date(Date.now() + 86400000).toISOString().split("T")[0];
      const wkRange = getWeekendDates();
      const wkStart = wkRange.start.toISOString().split("T")[0];
      const wkEnd = wkRange.end.toISOString().split("T")[0];
      let timeframeLabel: string | null = null;
      if (answers.timeframe.start === todayISO && answers.timeframe.end === todayISO) timeframeLabel = "today";
      else if (answers.timeframe.start === tomorrowISO && answers.timeframe.end === tomorrowISO) timeframeLabel = "tomorrow";
      else if (answers.timeframe.start === wkStart && answers.timeframe.end === wkEnd) timeframeLabel = "weekend";
      else if (answers.timeframe.start) timeframeLabel = "custom";

      // Map the budget-vibe excludes onto a single dollar cap.
      // ["low","medium","high"] = Free; ["medium","high"] = ≤$25; ["high"] = ≤$75; [] = no limit.
      const exc = answers.budgetExclude || [];
      const budgetMaxDollars = exc.includes("low") ? 0 : exc.includes("medium") ? 25 : exc.includes("high") ? 75 : null;

      const wp = answers.weatherExposure ?? 0;
      const weatherPreference = wp <= -1 ? "indoor" : wp >= 1 ? "outdoor" : "either";

      logWizardFn({ data: {
        session_id: sessionStorage.getItem("rp-session") || crypto.randomUUID(),
        timeframe_start: answers.timeframe.start || null,
        timeframe_end: answers.timeframe.end || null,
        timeframe_label: timeframeLabel,
        budget_level: exc.length ? exc.join(",") : null,
        budget_max_dollars: budgetMaxDollars,
        weather_exposure: answers.weatherExposure ?? 0,
        weather_preference: weatherPreference,
        duration_preference: answers.duration ?? null,
        origin_zip: answers.origin?.zip ?? null,
        max_drive_minutes: answers.origin?.maxDriveMinutes ?? null,
        extras_text: (answers.localPasses || "").slice(0, 500),
      } }).catch(() => {});
    }
    navigate({ to: "/explore" });
  };

  const selectedTimeframe = (() => {
    const t = answers.timeframe;
    if (!t.start) return -1;
    for (let i = 0; i < TIMEFRAME_OPTIONS.length; i++) {
      const r = TIMEFRAME_OPTIONS[i].getRange();
      if (r.start === t.start && r.end === t.end) return i;
    }
    return -1;
  })();

  const selectedBudget = !budgetChosen
    ? -1
    : BUDGET_VIBE_OPTIONS.findIndex(
        (o) => JSON.stringify(o.budgetExclude.sort()) === JSON.stringify([...answers.budgetExclude].sort()),
      );

  const handleDateDayClick = (date: Date) => {
    const clickedDate = toLocalDate(date);
    if (!dateRange.from || dateRange.to) {
      setDateRange({ from: clickedDate, to: undefined });
      return;
    }
    const from = toLocalDate(dateRange.from);
    const [start, end] = clickedDate.getTime() < from.getTime() ? [clickedDate, from] : [from, clickedDate];
    setDateRange({ from: start, to: end });
    update({ timeframe: { start: toDateString(start), end: toDateString(end) } });
    setShowDatePicker(false);
    setTimeout(() => advance(), 150);
  };

  const customRangeLabel = (() => {
    const from = dateRange.from ?? (selectedTimeframe === -1 && answers.timeframe.start ? new Date(`${answers.timeframe.start}T12:00:00`) : undefined);
    const to = dateRange.to ?? (selectedTimeframe === -1 && answers.timeframe.end ? new Date(`${answers.timeframe.end}T12:00:00`) : undefined);
    if (!from) return "Select a date or range";
    if (!to) return `${format(from, "MMM d")} →`;
    if (from.getTime() === to.getTime()) return format(from, "MMM d");
    return `${format(from, "MMM d")} – ${format(to, "MMM d")}`;
  })();

  // Fetch weather forecast when entering the weather step.
  useEffect(() => {
    if (step !== 3) return;
    if (!answers.timeframe.start || !answers.timeframe.end) return;
    if (weather || weatherLoading) return;
    setWeatherLoading(true);
    getWeatherForecast({ data: { startDate: answers.timeframe.start, endDate: answers.timeframe.end } })
      .then((w) => setWeather(w))
      .catch(() => {})
      .finally(() => setWeatherLoading(false));
  }, [step, answers.timeframe.start, answers.timeframe.end, weather, weatherLoading]);

  const weatherSummary = (() => {
    if (!weather || weather.days.length === 0) return null;
    const highs = weather.days.map((d) => d.tempHigh);
    const lows = weather.days.map((d) => d.tempLow);
    return { high: Math.max(...highs), low: Math.min(...lows), precip: weather.precipChance, days: weather.days };
  })();

  const exposureLabel = (val: WeatherExposure): { title: string; sub: string; emoji: string } => {
    switch (val) {
      case -2: return { title: "Tuck us inside", sub: "Climate-controlled, sheltered spots only", emoji: "🛋️" };
      case -1: return { title: "Mostly indoors", sub: "Quick outdoor jaunts are fine", emoji: "🏛️" };
      case 0:  return { title: "Roll with it", sub: "Mix of indoor & outdoor — surprise us", emoji: "🎲" };
      case 1:  return { title: "Lean outside", sub: "Open-air preferred unless it's miserable", emoji: "🌳" };
      case 2:  return { title: "Send us outside", sub: "Rain or shine — we live for the open air", emoji: "🌞" };
    }
  };

  const currentExposure = exposureLabel(answers.weatherExposure);

  const submitZip = useCallback(async (rawZip: string, maxDrive: number) => {
    const zip = rawZip.trim();
    if (!/^\d{5}$/.test(zip)) {
      setZipError("Please enter a 5-digit ZIP.");
      return;
    }
    setZipError(null);
    setZipLoading(true);
    try {
      const result = await geocodeZipFn({ data: { zip } });
      update({
        origin: {
          zip: result.zip,
          lat: result.lat,
          lng: result.lng,
          placeName: result.placeName,
          maxDriveMinutes: maxDrive,
        },
      });
      if (result.warning) setZipError(result.warning);
      else advance();
    } catch {
      setZipError("Couldn't look up that ZIP — try again or skip.");
    }
    setZipLoading(false);
  }, [advance, geocodeZipFn, update]);

  return (
    <div className="min-h-screen flex flex-col items-center justify-start px-4 pt-4 pb-6 bg-background">
      <div className="w-full max-w-md mb-3">
        <div className="flex items-center justify-between text-[11px] text-muted-foreground mb-1.5">
          <span>Step {step + 1} of {totalSteps}</span>
          <span>{Math.round(((step + 1) / totalSteps) * 100)}%</span>
        </div>
        <div className="h-2 rounded-full bg-foreground/10 overflow-hidden">
          <motion.div
            className="h-full rounded-full"
            style={{ background: "var(--color-primary)" }}
            animate={{ width: `${((step + 1) / totalSteps) * 100}%` }}
            transition={{ duration: 0.4 }}
          />
        </div>
      </div>

      <div className="w-full max-w-md">
        <AnimatePresence mode="wait">
          <motion.div
            key={step}
            initial={{ opacity: 0, x: 40 }}
            animate={{ opacity: 1, x: 0 }}
            exit={{ opacity: 0, x: -40 }}
            transition={{ duration: 0.3 }}
            className="rounded-3xl border border-foreground/10 p-5 shadow-xl"
            style={{ background: "var(--color-card)" }}
          >
            <h2 className="text-xl font-black text-foreground leading-tight" style={{ fontFamily: "'Syne', sans-serif" }}>{STEPS[step].title}</h2>
            <p className="text-muted-foreground text-xs mt-1 mb-4">{STEPS[step].subtitle}</p>

            {step === 0 && (
              <div className="space-y-2">
                {TIMEFRAME_OPTIONS.map((opt, i) => (
                  <OptionButton key={i} emoji={opt.emoji} label={opt.label} sublabel={opt.sublabel} selected={selectedTimeframe === i}
                    onClick={() => { setShowDatePicker(false); setDateRange({}); update({ timeframe: opt.getRange() }); advance(); }} />
                ))}
                <Popover open={showDatePicker} onOpenChange={setShowDatePicker}>
                  <PopoverTrigger asChild>
                    <button className={`flex items-center gap-3 w-full px-4 py-3 rounded-xl border border-accent/20 text-left transition-all text-foreground ${
                      selectedTimeframe === -1 && answers.timeframe.start ? "bg-accent/25 font-bold shadow-sm" : "bg-accent/10 hover:bg-accent/25"
                    }`}>
                      <span className="text-2xl">📆</span>
                      <div>
                        <span className="text-sm font-semibold block">Pick dates</span>
                        <span className="text-xs opacity-70">{customRangeLabel}</span>
                      </div>
                    </button>
                  </PopoverTrigger>
                  <PopoverContent className="w-auto p-0 bg-card border-border" align="center">
                    <div className="p-3 border-b border-border"><p className="text-xs text-muted-foreground">Tap a start date, then an end date</p></div>
                    <Calendar mode="range" min={1} selected={dateRange as any} onDayClick={handleDateDayClick}
                      disabled={(date) => date < new Date(new Date().setHours(0, 0, 0, 0))} initialFocus className={cn("p-3 pointer-events-auto")} />
                    {dateRange.from && !dateRange.to && (
                      <div className="px-3 pb-3">
                        <button onClick={() => {
                          const d = dateRange.from!;
                          setDateRange({ from: d, to: d });
                          const ds = toDateString(d);
                          update({ timeframe: { start: ds, end: ds } });
                          setShowDatePicker(false);
                          advance();
                        }} className="w-full text-center text-sm font-medium text-primary hover:underline">
                          Just {format(dateRange.from, "MMM d")} →
                        </button>
                      </div>
                    )}
                  </PopoverContent>
                </Popover>
              </div>
            )}

            {step === 1 && (
              <div className="space-y-2">
                {DURATION_OPTIONS.map((opt, i) => (
                  <OptionButton key={i} emoji={opt.emoji} label={opt.label} sublabel={opt.sublabel}
                    selected={answers.duration === opt.value}
                    onClick={() => { update({ duration: opt.value }); advance(); }} />
                ))}
              </div>
            )}

            {step === 2 && (
              <div className="space-y-2">
                {BUDGET_VIBE_OPTIONS.map((opt, i) => (
                  <OptionButton key={i} emoji={opt.emoji} label={opt.label} sublabel={opt.sublabel} selected={selectedBudget === i}
                    onClick={() => { setBudgetChosen(true); update({ budgetExclude: opt.budgetExclude }); advance(); }} />
                ))}
              </div>
            )}

            {step === 4 && (
              <div className="space-y-4">
                <div>
                  <label htmlFor="rp-zip" className="text-xs font-semibold text-muted-foreground uppercase tracking-wider">
                    Your ZIP code
                  </label>
                  <input
                    id="rp-zip"
                    type="text"
                    inputMode="numeric"
                    autoComplete="postal-code"
                    pattern="[0-9]{5}"
                    maxLength={5}
                    value={zipInput}
                    onChange={(e) => {
                      const next = e.target.value.replace(/\D/g, "").slice(0, 5);
                      setZipInput(next);
                      if (zipError) setZipError(null);
                    }}
                    placeholder="34202"
                    className="mt-1 w-full px-4 py-3 rounded-xl border-2 border-foreground/15 bg-foreground/5 text-2xl font-black tracking-widest text-center text-foreground focus:outline-none focus:border-primary transition-colors"
                    style={{ fontFamily: "'Syne', sans-serif" }}
                    disabled={zipLoading}
                  />
                  {zipError && <p className="mt-1.5 text-xs text-primary font-medium">{zipError}</p>}
                  {zipLoading && <p className="mt-1.5 text-xs text-muted-foreground">Looking that up…</p>}
                  {answers.origin?.placeName && !zipError && !zipLoading && (
                    <p className="mt-1.5 text-xs text-highlight font-bold">
                      ✓ {answers.origin.placeName}{answers.origin.zip ? ` · ${answers.origin.zip}` : ""}
                    </p>
                  )}
                </div>

                <div>
                  <p className="text-xs font-semibold text-muted-foreground uppercase tracking-wider mb-2">
                    How far will you drive?
                  </p>
                  <div className="grid grid-cols-4 gap-2">
                    {[15, 30, 60, 90].map((m) => (
                      <button
                        key={m}
                        type="button"
                        onClick={() => {
                          setMaxDriveMinutes(m);
                          if (answers.origin) {
                            update({ origin: { ...answers.origin, maxDriveMinutes: m } });
                          }
                        }}
                        className={`px-2 py-2.5 rounded-xl text-sm font-bold transition-all border ${
                          maxDriveMinutes === m
                            ? "bg-accent text-accent-foreground border-accent shadow-sm"
                            : "bg-accent/10 text-foreground border-accent/20 hover:bg-accent/25"
                        }`}
                      >
                        {m === 90 ? "90+" : m} min
                      </button>
                    ))}
                  </div>
                </div>
              </div>
            )}

            {step === 5 && (
              <div className="space-y-1">
                <Textarea
                  value={answers.localPasses}
                  onChange={(e) => update({ localPasses: e.target.value.slice(0, 500) })}
                  maxLength={500}
                  placeholder=""
                  rows={4}
                  className="resize-none bg-foreground/5 border-foreground/10 text-foreground placeholder:text-muted-foreground"
                />
                <div className={`text-[11px] text-right ${answers.localPasses.length > 450 ? "text-primary font-semibold" : "text-muted-foreground"}`}>
                  {answers.localPasses.length} / 500
                </div>
              </div>
            )}

            {step === 3 && (
              <div className="space-y-2">
                {/* Forecast preview */}
                {weatherLoading && (
                  <div className="rounded-2xl border border-foreground/10 p-3 bg-foreground/5 text-center text-sm text-muted-foreground">
                    Pulling the forecast for your dates…
                  </div>
                )}
                {!weatherLoading && weatherSummary && (
                  <div className="rounded-2xl border border-foreground/10 p-3 bg-foreground/5">
                    <div className="flex items-center justify-around text-center">
                      <div className="flex flex-col items-center gap-0.5">
                        <Thermometer className="h-4 w-4 text-primary" />
                        <span className="text-[10px] text-muted-foreground uppercase tracking-wider">High</span>
                        <span className="text-lg font-black text-foreground" style={{ fontFamily: "'Syne', sans-serif" }}>{weatherSummary.high}°</span>
                      </div>
                      <div className="flex flex-col items-center gap-0.5">
                        <Thermometer className="h-4 w-4 text-accent" />
                        <span className="text-[10px] text-muted-foreground uppercase tracking-wider">Low</span>
                        <span className="text-lg font-black text-foreground" style={{ fontFamily: "'Syne', sans-serif" }}>{weatherSummary.low}°</span>
                      </div>
                      <div className="flex flex-col items-center gap-0.5">
                        {weatherSummary.precip >= 40 ? <CloudRain className="h-4 w-4 text-accent" /> : <Droplets className="h-4 w-4 text-accent" />}
                        <span className="text-[10px] text-muted-foreground uppercase tracking-wider">Rain</span>
                        <span className="text-lg font-black text-foreground" style={{ fontFamily: "'Syne', sans-serif" }}>{weatherSummary.precip}%</span>
                      </div>
                    </div>
                    {weatherSummary.days.length > 1 && (
                      <div className="mt-2 pt-2 border-t border-foreground/10 flex justify-around text-[11px] text-muted-foreground">
                        {weatherSummary.days.slice(0, 5).map((d) => (
                          <div key={d.date} className="text-center">
                            <div>{new Date(`${d.date}T12:00:00`).toLocaleDateString("en-US", { weekday: "short" })}</div>
                            <div className="text-base">{d.icon}</div>
                            <div className="font-semibold text-foreground">{d.tempHigh}°</div>
                          </div>
                        ))}
                      </div>
                    )}
                  </div>
                )}
                {!weatherLoading && !weatherSummary && (
                  <div className="rounded-2xl border border-foreground/10 p-3 bg-foreground/5 text-center text-xs text-muted-foreground">
                    Forecast unavailable for these dates — pick your vibe anyway.
                  </div>
                )}

                {/* Simple 3-option pick */}
                {([
                  { emoji: "🛋️", label: "Indoors", sublabel: "Climate-controlled & sheltered", value: -2 as WeatherExposure },
                  { emoji: "🎲", label: "Either works", sublabel: "Mix of indoor & outdoor", value: 0 as WeatherExposure },
                  { emoji: "🌞", label: "Outdoors", sublabel: "Open-air, fresh-air picks", value: 2 as WeatherExposure },
                ]).map((opt, i) => (
                  <OptionButton
                    key={i}
                    emoji={opt.emoji}
                    label={opt.label}
                    sublabel={opt.sublabel}
                    selected={exposureTouched && answers.weatherExposure === opt.value}
                    onClick={() => { setExposureTouched(true); update({ weatherExposure: opt.value }); advance(); }}
                  />
                ))}
              </div>
            )}

          </motion.div>
        </AnimatePresence>

        <div className="flex items-center justify-between mt-4">
          <button
            onClick={() => setStep((s) => Math.max(0, s - 1))}
            disabled={step === 0}
            className="inline-flex items-center gap-1.5 rounded-full px-4 py-2.5 text-sm font-bold border-2 border-primary text-primary hover:bg-primary/10 disabled:opacity-30 disabled:hover:bg-transparent transition-colors"
          >
            <ArrowLeft className="h-4 w-4" /> Back
          </button>
          {step === totalSteps - 1 ? (
            <button onClick={handleFinish}
              className="inline-flex items-center gap-2 rounded-full px-8 py-3.5 text-base font-bold text-white hover:opacity-90 transition-all shadow-lg"
              style={{ background: "var(--color-primary)", fontFamily: "'Syne', sans-serif" }}>
              <Rocket className="h-4 w-4" /> Let's Go
            </button>
          ) : (
            <button
              onClick={() => {
                if (step === 4) {
                  // Origin step: submit zip (or default) before advancing
                  submitZip(zipInput.trim() || DEFAULT_ZIP, maxDriveMinutes);
                  return;
                }
                advance();
              }}
              disabled={step === 4 && zipLoading}
              className="inline-flex items-center gap-1.5 rounded-full px-6 py-2.5 text-sm font-bold text-white hover:opacity-90 transition-opacity shadow-md disabled:opacity-50"
              style={{ background: "var(--color-primary)" }}
            >
              {step === 4 && zipLoading ? "Checking…" : "Next"} <ArrowRight className="h-4 w-4" />
            </button>
          )}
        </div>
      </div>
    </div>
  );
}
